import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';

/* ─── ANIMATIONS ──────────────────────────────────────────────── */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drawLine = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const softGlow = keyframes`
  0%, 100% { opacity: 0.5; }
  50%       { opacity: 1; }
`;

/* ─── SECTION SHELL ───────────────────────────────────────────── */

const Section = styled.section`
  position: relative;
  background: #f7f5ef;
  overflow: hidden;
  padding: 0;
`;

/* ─── TOP CREAM ZONE ──────────────────────────────────────────── */

const TopZone = styled.div`
  position: relative;
  z-index: 2;
  padding: 5.5rem 6% 4rem;
  text-align: center;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 4rem 6% 3rem;
  }

  @media (max-width: 768px) {
    padding: 3.5rem 5% 2.5rem;
  }
`;

/* ─── EYEBROW ─────────────────────────────────────────────────── */

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: 'Times New Roman', serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #3730A3;
  margin-bottom: 1.8rem;
  animation: ${fadeUp} 0.7s ease 0.1s both;

  &::before,
  &::after {
    content: '';
    display: block;
    width: 36px;
    height: 1px;
    background: #3730A3;
    transform-origin: left;
    animation: ${drawLine} 0.6s ease 0.4s both;
  }

  @media (max-width: 768px) {
    font-size: 0.65rem;
    gap: 8px;
    &::before, &::after { width: 22px; }
  }
`;

/* ─── MAIN HEADLINE ───────────────────────────────────────────── */

const Headline = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: clamp(2rem, 4.5vw, 3.6rem);
  font-weight: 900;
  line-height: 1.1;
  color: #111128;
  letter-spacing: -0.03em;
  max-width: 820px;
  margin: 0 auto 1.4rem;
  animation: ${fadeUp} 0.7s ease 0.2s both;

  em {
    font-style: normal;
    color: #3730A3;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 3px;
      background: #ffd966;
      border-radius: 2px;
      transform-origin: left;
      animation: ${drawLine} 0.7s ease 0.8s both;
    }
  }
`;

/* ─── SUB DESCRIPTION ─────────────────────────────────────────── */

const SubDesc = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: clamp(0.9rem, 1.4vw, 1.1rem);
  color: #4a4a6a;
  line-height: 1.75;
  max-width: 560px;
  margin: 0 auto 2.8rem;
  animation: ${fadeUp} 0.7s ease 0.3s both;
`;

/* ─── CHECK ROW ───────────────────────────────────────────────── */

const CheckRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  flex-wrap: wrap;
  margin-bottom: 3rem;
  animation: ${fadeUp} 0.7s ease 0.4s both;

  @media (max-width: 768px) {
    gap: 1.2rem;
    flex-direction: column;
    align-items: center;
  }
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  font-family: 'Times New Roman', serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a2e;

  svg {
    color: #3730A3;
    font-size: 1rem;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.88rem;
  }
`;

/* ─── DIAGONAL RIBBON ─────────────────────────────────────────── */

const Ribbon = styled.div`
  position: relative;
  background: linear-gradient(135deg, #0b0b77 0%, #1e1b8e 40%, #3730A3 100%);
  padding: 4.5rem 6%;
  z-index: 2;
  overflow: hidden;

  /* Top diagonal cut */
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    right: 0;
    height: 100px;
    background: #f7f5ef;
    clip-path: polygon(0 0, 100% 0, 100% 30%, 0 100%);
    z-index: 1;
  }

  /* Bottom diagonal cut */
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: 0;
    right: 0;
    height: 100px;
    background: #f7f5ef;
    clip-path: polygon(0 70%, 100% 0, 100% 100%, 0 100%);
    z-index: 1;
  }

  /* Subtle grid texture */
  .ribbon-inner {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 44px 44px;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 4rem 5%;
  }
`;

const RibbonInner = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const RibbonLabel = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  color: rgba(196, 181, 253, 0.7);
  margin-bottom: 1.2rem;
`;

const RibbonTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -0.02em;
  margin-bottom: 0.6rem;
`;

const RibbonSub = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.95rem;
  color: rgba(255,255,255,0.55);
  margin-bottom: 3rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.88rem;
    margin-bottom: 2.2rem;
  }
`;

/* ─── STEP CARDS ──────────────────────────────────────────────── */

const StepsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 2rem 2rem 1.8rem;
  width: 260px;
  text-align: left;
  position: relative;
  animation: ${fadeUp} 0.7s ease ${props => props.delay || '0s'} both;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
  }

  @media (max-width: 1100px) {
    width: 220px;
    padding: 1.6rem 1.6rem 1.5rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 320px;
  }
`;

const StepNumber = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.6rem;
  letter-spacing: -2px;

  @media (max-width: 1100px) {
    font-size: 2.8rem;
  }
`;

const StepDivider = styled.div`
  width: 28px;
  height: 2px;
  background: #a78bfa;
  border-radius: 1px;
  margin-bottom: 0.8rem;
  animation: ${softGlow} 3s ease infinite;
`;

const StepText = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
  line-height: 1.5;

  @media (max-width: 1100px) {
    font-size: 0.85rem;
  }
`;

/* ─── BOTTOM CTA ZONE ─────────────────────────────────────────── */

const BottomZone = styled.div`
  position: relative;
  z-index: 2;
  padding: 4rem 6% 5.5rem;
  text-align: center;
  animation: ${fadeUp} 0.7s ease 0.5s both;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 3.5rem 6% 4.5rem;
  }

  @media (max-width: 768px) {
    padding: 3rem 5% 4rem;
  }
`;

const CTAButton = styled(Link)`
  font-family: 'Times New Roman', serif;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #3730A3;
  color: #ffffff;
  padding: 1.05rem 2.8rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.3px;
  box-shadow: 0 10px 35px rgba(55, 48, 163, 0.28);
  transition: all 0.3s ease;

  &:hover {
    background: #0b0b77;
    transform: translateY(-3px);
    box-shadow: 0 16px 42px rgba(55, 48, 163, 0.38);
  }

  svg.arrow {
    transition: transform 0.3s ease;
  }

  &:hover svg.arrow {
    transform: translateX(5px);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.9rem 2.3rem;
    font-size: 0.92rem;
  }

  @media (max-width: 768px) {
    padding: 0.95rem 2rem;
    font-size: 0.9rem;
    width: 100%;
    max-width: 320px;
    justify-content: center;
  }
`;

const NoteText = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.82rem;
  color: #8a8aaa;
  margin-top: 1.2rem;
  font-style: italic;
`;

/* ─── DECORATIVE SIDE RULE ────────────────────────────────────── */

const SideRule = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.side}: 5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  z-index: 2;

  span {
    display: block;
    width: 1px;
    background: rgba(55, 48, 163, 0.2);
    height: ${props => props.h || '60px'};
    border-radius: 1px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

/* ─── COMPONENT ───────────────────────────────────────────────── */

const FreeDemoBanner = () => {
  return (
    <Section>
      {/* Left decorative rule */}
      <SideRule side="left">
        <span style={{ height: '80px' }} />
        <span style={{ height: '10px', opacity: 0.5 }} />
        <span style={{ height: '40px' }} />
      </SideRule>

      {/* Right decorative rule */}
      <SideRule side="right">
        <span style={{ height: '40px' }} />
        <span style={{ height: '10px', opacity: 0.5 }} />
        <span style={{ height: '80px' }} />
      </SideRule>

      {/* ── TOP: Headline + Checks ── */}
      <TopZone>
        <Eyebrow>Free Career Guidance</Eyebrow>

        <Headline>
          Not Sure Which <em>Certification</em><br />is Right For You?
        </Headline>

        <SubDesc>
          Book a free one-on-one consultation with our career experts. We'll help you
          choose the perfect certification path based on your background and goals.
        </SubDesc>

        <CheckRow>
          <CheckItem><FaCheckCircle /> Personalized career roadmap</CheckItem>
          <CheckItem><FaCheckCircle /> Understand salary &amp; job opportunities</CheckItem>
          <CheckItem><FaCheckCircle /> Get answers to all your questions</CheckItem>
        </CheckRow>
      </TopZone>

      {/* ── RIBBON: How It Works ── */}
      <Ribbon>
        <div className="ribbon-inner" />
        <RibbonInner>
          <RibbonLabel>How It Works</RibbonLabel>
          <RibbonTitle>A Simple 3-Step Process</RibbonTitle>
          <RibbonSub>
            Get started on your healthcare career journey in minutes
          </RibbonSub>

          <StepsRow>
            <StepCard delay="0.1s">
              <StepNumber>01</StepNumber>
              <StepDivider />
              <StepText>Pick a convenient time slot</StepText>
            </StepCard>

            <StepCard delay="0.22s">
              <StepNumber>02</StepNumber>
              <StepDivider />
              <StepText>Speak with our career expert</StepText>
            </StepCard>

            <StepCard delay="0.34s">
              <StepNumber>03</StepNumber>
              <StepDivider />
              <StepText>Get your personalized learning plan</StepText>
            </StepCard>
          </StepsRow>
        </RibbonInner>
      </Ribbon>

      {/* ── BOTTOM: CTA ── */}
      <BottomZone>
        <CTAButton to="/contact-us">
          <FaCalendarAlt /> Book Free Consultation <FaArrowRight className="arrow" />
        </CTAButton>
        <NoteText>100% free · No commitment · 15-minute session</NoteText>
      </BottomZone>
    </Section>
  );
};

export default FreeDemoBanner;